import { useMemo } from 'react';
import { ActiveSession } from './useDashboardData';
import { 
  isToday, 
  isThisWeek, 
  isThisMonth, 
  isThisQuarter,
  parseISO 
} from 'date-fns';

export type SortField = 'date' | 'clarity' | 'phase' | 'title';
export type SortDirection = 'asc' | 'desc';

export interface SortOptions {
  field: SortField;
  direction: SortDirection;
}

export interface SessionFilters {
  phase: string;
  decisionArea: string;
  dateRange: string;
  searchQuery: string;
}

export function useSessionFilters(
  sessions: ActiveSession[],
  filters: SessionFilters,
  sortOptions: SortOptions
) {
  const filteredAndSortedSessions = useMemo(() => {
    // First, filter sessions
    const filtered = sessions.filter((session) => {
      // Phase filter
      if (filters.phase !== 'all') {
        if (session.phase.toString() !== filters.phase) {
          return false;
        }
      }

      // Decision area filter
      if (filters.decisionArea !== 'all') {
        if (session.decision_type !== filters.decisionArea) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateRange !== 'all') {
        try {
          const sessionDate = parseISO(session.updated_at);
          
          switch (filters.dateRange) {
            case 'today':
              if (!isToday(sessionDate)) return false;
              break;
            case 'week':
              if (!isThisWeek(sessionDate, { weekStartsOn: 1 })) return false;
              break;
            case 'month':
              if (!isThisMonth(sessionDate)) return false;
              break;
            case 'quarter':
              if (!isThisQuarter(sessionDate)) return false;
              break;
          }
        } catch {
          // If date parsing fails, include the session
          return true;
        }
      }

      // Search query filter
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const searchableText = [
          session.summary || '',
          session.decision_type || '',
          session.decision_subtype || '',
        ].join(' ').toLowerCase();

        if (!searchableText.includes(query)) {
          return false;
        }
      }

      return true;
    });

    // Then, sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortOptions.field) {
        case 'date':
          try {
            const dateA = new Date(a.updated_at).getTime();
            const dateB = new Date(b.updated_at).getTime();
            comparison = dateB - dateA; // Newer first by default
          } catch {
            comparison = 0;
          }
          break;

        case 'clarity':
          // Simplified clarity calculation (would use actual data in production)
          const clarityA = a.phase === 3 ? 100 : a.phase === 2 ? 70 : 45;
          const clarityB = b.phase === 3 ? 100 : b.phase === 2 ? 70 : 45;
          comparison = clarityA - clarityB;
          break;

        case 'phase':
          comparison = a.phase - b.phase;
          break;

        case 'title':
          const titleA = (a.summary || a.decision_type || '').toLowerCase();
          const titleB = (b.summary || b.decision_type || '').toLowerCase();
          comparison = titleA.localeCompare(titleB);
          break;
      }

      return sortOptions.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [sessions, filters, sortOptions]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.phase !== 'all') count++;
    if (filters.decisionArea !== 'all') count++;
    if (filters.dateRange !== 'all') count++;
    if (filters.searchQuery.trim()) count++;
    return count;
  }, [filters]);

  return {
    filteredSessions: filteredAndSortedSessions,
    activeFilterCount,
  };
}
